/**
 * useCountUp — anima un número desde 0 hasta `target` en `durationMs`.
 *
 * Usado en los KPIs del Dashboard para dar un "tick" visual cuando el valor
 * aparece (paquete Y). Es decoración pura: la lectura final es la real, y
 * cualquier consumer que lea el output recibe siempre el target post-mount.
 *
 * Reglas (brandbook §"Motion"):
 *   - Easing easeOutQuart: ramp rápido al inicio, asentar suave al final.
 *   - Duración 600ms (slow para que el ojo lea el "tick" pero no aburra).
 *   - Respeta `prefers-reduced-motion`: si el usuario la pide, devuelve el
 *     target directo sin animar (a11y).
 *
 * Si el target cambia durante la animación, retoma desde el valor actual
 * hacia el nuevo target (no resetea a 0). Esto evita parpadeos cuando el
 * dashboard re-fetchea KPIs.
 */

import { useEffect, useRef, useState } from 'react';

const DEFAULT_DURATION = 600;

export function useCountUp(target: number, durationMs: number = DEFAULT_DURATION): number {
  const [value, setValue] = useState<number>(() => {
    if (typeof window === 'undefined') return target;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    return reducedMotion ? target : 0;
  });
  const rafRef = useRef<number | null>(null);
  const startedRef = useRef<{ from: number; to: number; t0: number } | null>(null);

  useEffect(() => {
    // SSR / no window
    if (typeof window === 'undefined') {
      setValue(target);
      return;
    }
    // Respetar reduced-motion
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      setValue(target);
      return;
    }
    // Si el target es igual al value actual, no hacemos nada
    if (value === target) return;

    const from = value;
    const to = target;
    const t0 = performance.now();
    startedRef.current = { from, to, t0 };

    const tick = (now: number) => {
      const elapsed = now - t0;
      const progress = Math.min(1, elapsed / durationMs);
      // easeOutQuart: 1 - (1-x)^4 — fuerte ramp inicial, asentado suave al final
      const eased = 1 - Math.pow(1 - progress, 4);
      const current = Math.round(from + (to - from) * eased);
      setValue(current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setValue(to);
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return value;
}
